import { useEffect, useState, memo, useCallback, useRef } from 'react'
import Styles from './CampaignGenerationLoadingScreen.module.scss'
import { BlazeLogo } from 'components/MultiStepFlow/MultiStepPageLayout/BlazeLogo'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { useCampaignGenerationLoadingContext } from './CampaignGenerationLoadingContext'
import { Button } from 'blaze-ui/Button'
import { Logout1, Calendar2 } from 'blaze-ui/icons'
import { useSession } from 'hooks/useSession'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useSchedules } from 'hooks/queries/workspaces/schedules/useSchedules'
import { FinalStep } from 'screens/VirtualMarketerOnboarding/FinalStep/FinalStep'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'
import { useUpdateUserAppPreferences } from 'hooks/queries/useUpdateUserAppPreferences'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { AIJobStatus } from 'types'

const CONFIG = {
  ANIMATION: {
    wordDelay: 80,
    baseDuration: 400,
    cycleInterval: 5000,
  },
  STATUS_CYCLE_INTERVAL: 3000,
  STATUS_FADE_DURATION: 400,
  NAVIGATION_DELAY: 1000,
} as const

const CYCLING_TEXTS = [
  [
    ['Crafting', 'strategic'],
    ['campaigns', 'for', 'your', 'brand'],
  ],
  [
    ['Analyzing', 'your', 'market'],
    ['and', 'audience'],
  ],
  [
    ['Generating', 'campaign'],
    ['ideas', 'that', 'convert'],
  ],
  [
    ['Building', 'a', 'roadmap'],
    ['to', 'grow', 'your', 'business'],
  ],
  [
    ['Planning', 'content'],
    ['for', 'every', 'season'],
  ],
] as const

const STATUS_TEXTS = [
  'Generating campaigns...',
  'Determining goals',
  'Segmenting audiences',
  'Creating themes',
  'Aligning schedules',
] as const

const PHASE_CLASS_MAP = {
  exiting: Styles.wordExiting,
  entering: Styles.wordEntering,
  visible: Styles.wordVisible,
} as const

const PLACEHOLDER_CONFIG = [
  { id: 2, showMiddle: true },
  { id: 3, showMiddle: true },
  { id: 4, showMiddle: false },
  { id: 5, showMiddle: true },
  { id: 6, showMiddle: true },
  { id: 7, showMiddle: false },
  { id: 8, showMiddle: true },
] as const

const TIMELINE_MONTHS = [
  { position: '25%', offset: 1 },
  { position: '83%', offset: 2 },
] as const

interface PlaceholderCardProps {
  placeholderId: number
  showMiddle: boolean
}

const PlaceholderCard = memo(({ placeholderId, showMiddle }: PlaceholderCardProps) => (
  <div className={`${Styles.scrollingPlaceholder} ${Styles[`placeholder${placeholderId}`]}`}>
    <div className={Styles.placeholderContent}>
      <div className={Styles.placeholderTop}>
        <div className={Styles.placeholderTopLeft}>
          <Calendar2 className={Styles.calendarIcon} />
          <div className={Styles.loadingBar} />
          <div className={Styles.divider} />
          <div className={Styles.loadingBar} />
        </div>
        <div className={Styles.loadingBar} />
      </div>
      {showMiddle && <div className={Styles.loadingBarMiddle} />}
      <div className={Styles.loadingBarBottom} />
    </div>
  </div>
))

PlaceholderCard.displayName = 'PlaceholderCard'

interface TimelineMonthProps {
  position: string
  monthName: string
}

const TimelineMonth = memo(({ position, monthName }: TimelineMonthProps) => (
  <div className={Styles.monthTimelineIndicator} style={{ top: position }}>
    <div className={Styles.monthTimelineContent}>
      <div className={Styles.grayLine} />
    </div>
    <span className={Styles.monthLabel}>{monthName}</span>
  </div>
))

TimelineMonth.displayName = 'TimelineMonth'

interface CyclingTextProps {
  className?: string
}

const CyclingText = memo(({ className }: CyclingTextProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0)
  const [animationPhase, setAnimationPhase] = useState<'visible' | 'exiting' | 'entering'>('visible')

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = []

    const interval = setInterval(() => {
      const currentText = CYCLING_TEXTS[currentTextIndex]
      const totalWords = currentText.flat().length

      setAnimationPhase('exiting')

      const exitDuration = totalWords * CONFIG.ANIMATION.wordDelay + CONFIG.ANIMATION.baseDuration
      const exitTimeout = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % CYCLING_TEXTS.length)
        setAnimationPhase('entering')
      }, exitDuration)
      timeouts.push(exitTimeout)

      const newText = CYCLING_TEXTS[(currentTextIndex + 1) % CYCLING_TEXTS.length]
      const newTotalWords = newText.flat().length
      const enterDuration = exitDuration + newTotalWords * CONFIG.ANIMATION.wordDelay + CONFIG.ANIMATION.baseDuration
      const enterTimeout = setTimeout(() => {
        setAnimationPhase('visible')
      }, enterDuration)
      timeouts.push(enterTimeout)
    }, CONFIG.ANIMATION.cycleInterval)

    return () => {
      clearInterval(interval)
      timeouts.forEach(clearTimeout)
    }
  }, [currentTextIndex])

  const renderText = useCallback(
    (textLines: readonly (readonly string[])[]) => {
      let globalWordIndex = 0

      return textLines.map((line, lineIndex) => (
        <div key={lineIndex} className={Styles.textLine}>
          {line.map((word, wordInLineIndex) => {
            const currentGlobalIndex = globalWordIndex++
            const phaseClass = PHASE_CLASS_MAP[animationPhase]
            const animationDelay =
              animationPhase !== 'visible' ? `${currentGlobalIndex * CONFIG.ANIMATION.wordDelay}ms` : undefined

            return (
              <span
                key={`${word}-${lineIndex}-${wordInLineIndex}-${animationPhase}`}
                className={`${Styles.word} ${phaseClass} ${Styles.wordAnimated}`}
                style={{ '--animation-delay': animationDelay } as React.CSSProperties}
              >
                {word}
              </span>
            )
          })}
        </div>
      ))
    },
    [animationPhase],
  )

  return (
    <div className={`${Styles.cyclingTextContainer} ${className || ''}`}>
      <h1 className={Styles.mainTitle}>{renderText(CYCLING_TEXTS[currentTextIndex])}</h1>
    </div>
  )
})

CyclingText.displayName = 'CyclingText'

const CyclingStatusText = memo(() => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false)
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % STATUS_TEXTS.length)
        setIsVisible(true)
      }, CONFIG.STATUS_FADE_DURATION)
    }, CONFIG.STATUS_CYCLE_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  return (
    <p className={`${Styles.loadingSubtext} ${isVisible ? Styles.statusVisible : Styles.statusHidden}`}>
      {STATUS_TEXTS[currentIndex]}
    </p>
  )
})

CyclingStatusText.displayName = 'CyclingStatusText'

export interface CampaignGenerationLoadingScreenProps {
  brandPlanId: number
  brandProfileId?: number
  analysisId?: number
  campaignData?: {
    growth_goals_input: string
    key_dates_input: string
  }
  startDate?: string
  onComplete: () => void
  onBack: () => void
  className?: string
}

export const CampaignGenerationLoadingScreen = memo(
  ({
    brandPlanId,
    brandProfileId,
    analysisId,
    campaignData,
    startDate,
    onComplete,
    onBack,
    className,
  }: CampaignGenerationLoadingScreenProps) => {
    const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
    const { mutateAsync: updatePreferences } = useUpdateUserAppPreferences()
    const [showFinalStep, setShowFinalStep] = useState(!!isOnboardingStrategiesEnabled)
    const { logout } = useSession()
    const schedulesQuery = useSchedules({
      orderBy: 'start_date',
      orderDirection: 'asc',
      items: 15,
      startDate: dayjs().format('YYYY-MM-DD'),
    })
    const { setProgress, loadingPercentage } = useCampaignGenerationLoadingContext()
    const { mutateAsync: startBrandPlan } = useStartBrandPlan({ brandPlanId })
    const { mutateAsync: onboard } = useOnboard()
    const hasStartedGeneratingRef = useRef(false)

    const shouldPollForProfile =
      isOnboardingStrategiesEnabled && brandProfileId != null && analysisId != null
    const { data: analysisData } = usePollBrandProfileAnalysis(
      { id: analysisId ?? 0, brandProfileId: brandProfileId ?? 0 },
      { enabled: shouldPollForProfile },
    )

    const handleLogout = useCallback(async () => {
      await logout()
    }, [logout])

    const analysisStatus = analysisData?.brand_profile_analysis?.status
    const isAnalysisComplete = analysisStatus === AIJobStatus.Successful
    const isAnalysisFailed =
      analysisStatus === AIJobStatus.Failed || analysisStatus === AIJobStatus.Expired
    const canStartBrandPlan = !shouldPollForProfile || isAnalysisComplete

    useEffect(() => {
      if (shouldPollForProfile) {
        setProgress(10)
      }
    }, [shouldPollForProfile, setProgress])

    useEffect(() => {
      if (shouldPollForProfile && isAnalysisFailed) {
        showErrorMessageToast('Brand profile analysis failed. Please try again.')
        onBack()
      }
    }, [shouldPollForProfile, isAnalysisFailed, onBack])

    useEffect(() => {
      if (!canStartBrandPlan || hasStartedGeneratingRef.current) return
      hasStartedGeneratingRef.current = true

      const abortController = new AbortController()

      const startGenerating = async () => {
        try {
          if (!shouldPollForProfile) {
            setProgress(10)
          }

          await startBrandPlan({
            growth_goals_input: campaignData?.growth_goals_input,
            key_dates_input: campaignData?.key_dates_input,
            start_date: startDate,
            signal: abortController.signal,
          })

          await schedulesQuery.refetch()

          setProgress(80)

          await onboard()
          if (isOnboardingStrategiesEnabled) await updatePreferences({ scheduledPostsView: 'calendar' }) // Make sure user lands on calendar view

          setProgress(100)

          onComplete()
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return
          }
          showErrorMessageToast('Failed to generate campaigns. Please try again.')
          onBack()
        }
      }

      startGenerating()

      return () => {
        abortController.abort()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canStartBrandPlan])

    if (showFinalStep) {
      return <FinalStep onNext={() => setShowFinalStep(false)} />
    }

    return (
      <div className={`${Styles.loadingContainer} ${className || ''}`}>
        <div className={Styles.logo}>
          <BlazeLogo width={87} />
        </div>

        <div className={Styles.signOut}>
          <Button variant="ghost" size="xs" color="var(--light-40)" frontIcon={Logout1} onPress={handleLogout}>
            Sign out
          </Button>
        </div>

        <header className={Styles.header}>
          <div className={Styles.timelineIndicator}>
            <div className={Styles.timelineIndicatorContent}>
              <div className={Styles.blueLine} />
              <div className={Styles.todayContainer}>
                <div className={Styles.blueDot} />
              </div>
            </div>
            <span className={Styles.todayLabel}>Today</span>
          </div>
        </header>

        {TIMELINE_MONTHS.map(({ position, offset }) => (
          <TimelineMonth key={offset} position={position} monthName={dayjs().add(offset, 'month').format('MMMM')} />
        ))}

        <div className={Styles.scrollingPlaceholdersContainer}>
          {PLACEHOLDER_CONFIG.map(({ id, showMiddle }) => (
            <PlaceholderCard key={id} placeholderId={id} showMiddle={showMiddle} />
          ))}
        </div>

        <div className={Styles.contentWrapper}>
          <CyclingText />
          <div className={Styles.loadingTextWrapper}>
            <CyclingStatusText />
            <div className={Styles.styledTextContainer}>
              <span className={Styles.styledTextContent}>{loadingPercentage}%</span>
            </div>
          </div>
        </div>
      </div>
    )
  },
)

CampaignGenerationLoadingScreen.displayName = 'CampaignGenerationLoadingScreen'
